import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActiveReceptionLanguage } from '../../reception/locale';
import { useDepartmentLocale } from '../../ui/locale/departmentLocale';
import { ACCOUNT_LANGUAGE_EVENT, getAccountLanguage } from './preferenceEvents';
import { useOptionalLanguage } from '../../context/LanguageContext';
import { translateUiText } from '../../ui/locale/centralLocalization';

/** Shared overlays sit above account context; follow its published display language. */
export function useAccountPresentationLocale() {
  const appLocale = useOptionalLanguage();
  const department = useDepartmentLocale();
  const activeReception = useActiveReceptionLanguage();
  const [accountLanguage, setAccountLanguage] = useState(getAccountLanguage);
  useEffect(() => {
    setAccountLanguage(getAccountLanguage());
    const changed = () => setAccountLanguage(getAccountLanguage());
    window.addEventListener(ACCOUNT_LANGUAGE_EVENT, changed);
    return () => window.removeEventListener(ACCOUNT_LANGUAGE_EVENT, changed);
  }, []);
  const language = appLocale?.language ?? department?.language ?? activeReception ?? accountLanguage ?? 'en';
  const namespace = activeReception !== null ? 'reception' : department ? 'sales' : 'crm';
  const t = useCallback(<T,>(value: T): T =>
    (typeof value === 'string' ? translateUiText(value, language, namespace) : value) as T,
  [language, namespace]);
  return useMemo(() => ({ t, language }), [t, language]);
}

/** Existing consumers can continue to use the text-only helper. */
export function useAccountPresentationText() {
  return useAccountPresentationLocale().t;
}
